import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { TbCirclePlus, TbKey, TbRefresh } from "react-icons/tb";
import {
  GRID_HISTORY_COLS_5,
  GRID_HISTORY_COLS_6,
  GridHistoryCard,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryDetailButton,
  GridHistoryHeader,
  GridHistoryRow,
  GridHistoryStatusActionsHeader,
  GridPagination,
} from "../../../../Components/Molecules/GridHistory";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { useToast } from "../../../../Hooks/toast";
import {
  fetchBaasPeerInstanceRoots,
  getPrimaryBalance,
  formatBaasAvailableWithLimit,
} from "../../../../Services/baasAccountsListApi";
import { fetchBaasVirtualAccounts } from "../../../../Services/baasVirtualAccountsApi";
import { formatMonetaryValue } from "../../../../Utils/formatters";
import { isUnavailablePldProposalSituation } from "../../../../Utils/baasProposalSituation";
import { paginateList } from "../../Extract/extractFilters";
import { hasBaasBankingRails } from "../baasCapabilities";
import { BaasTablePageSkeleton } from "../components/BaasSkeleton";
import { buildBaasAccountDetailPath } from "../constants";
import { BaasInstanceNameCell } from "../components/BaasInstanceNameCell";
import {
  canUseBaasGlobalInstanceFilter,
  resolveBaasListInstanceNumericId,
  rowMatchesBaasListInstanceFilter,
  shouldFetchBaasPeerLists,
  stampBaasRowInstance,
} from "../baasInstanceListFilter";
import { shouldPinPeerForAccountOps } from "../baasPeerOpsPin";
import { BaasAccountPixKeysModal } from "./components/BaasAccountPixKeysModal";
import { BaasAccountStatusBadge } from "./components/BaasAccountStatusBadge";
import { BaasProposalSituationBadge } from "../components/BaasProposalSituationBadge";
import { BaasVirtualAccountCreateModal } from "./components/BaasVirtualAccountCreateModal";

const formatBalanceSummary = (account) => {
  const primary = getPrimaryBalance(account.balances);
  if (!primary) return intl.get("baas.accounts.noBalances");
  return formatBaasAvailableWithLimit(primary);
};

const VirtualAccountsHeader = ({ showInstanceColumn }) => (
  <GridHistoryHeader colSpan={showInstanceColumn ? 6 : 5}>
    <span>{intl.get("baas.accounts.columns.entity")}</span>
    <span>{intl.get("baas.accounts.columns.account")}</span>
    <span>{intl.get("baas.accounts.columns.type")}</span>
    {showInstanceColumn ? (
      <span>{intl.get("baas.accounts.columns.instance")}</span>
    ) : null}
    <span>{intl.get("baas.accounts.columns.balances")}</span>
    <GridHistoryStatusActionsHeader statusLabel={intl.get("baas.accounts.columns.status")} />
  </GridHistoryHeader>
);

const VirtualAccountRow = ({ account, onOpen, onPixKeys, canViewPix, showInstanceColumn }) => {
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen(account);
    }
  };

  return (
    <GridHistoryRow
      colSpan={showInstanceColumn ? 6 : 5}
      className="cursor-pointer hover:bg-gray-50/60 dark:hover:bg-brand-dark-elevated/30"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(account)}
      onKeyDown={handleKeyDown}
    >
      <GridHistoryCell label={intl.get("baas.accounts.columns.entity")}>
        <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
          {String(account.entityName || "").trim().toUpperCase() || "—"}
        </p>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.accounts.columns.account")}>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
            {String(
              account.accountNumber
              || account.alias
              || account.name
              || account.id
              || "",
            ).trim().toUpperCase() || "—"}
          </p>
          <p className="truncate font-mono text-xs text-gray-500 dark:text-gray-400">
            {account.id}
          </p>
        </div>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("baas.accounts.columns.type")}>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {account.isInstanceRoot || account.type === "virtual"
            ? intl.get("baas.accounts.type.virtual")
            : intl.get(`baas.accounts.type.${account.type}`) || account.type || "—"}
        </p>
      </GridHistoryCell>
      {showInstanceColumn ? (
        <GridHistoryCell label={intl.get("baas.accounts.columns.instance")}>
          <BaasInstanceNameCell name={account.instanceName || account.programCode} />
        </GridHistoryCell>
      ) : null}
      <GridHistoryCell label={intl.get("baas.accounts.columns.balances")}>
        <p
          className="truncate text-sm text-gray-600 dark:text-gray-300"
          title={formatBalanceSummary(account)}
        >
          {formatBalanceSummary(account)}
        </p>
      </GridHistoryCell>
      <div className="flex flex-wrap items-center gap-2.5 lg:justify-end">
        <BaasAccountStatusBadge status={account.status} />
        {account.proposalSituation && !isUnavailablePldProposalSituation(account.proposalSituation) ? (
          <BaasProposalSituationBadge situation={account.proposalSituation} />
        ) : null}
        {canViewPix ? (
          <button
            type="button"
            className="grid-history-action-btn grid-history-action-btn--icon !h-8 !w-8 !min-h-8 !min-w-8"
            aria-label={intl.get("baas.accounts.quickActions.pixKeys")}
            title={intl.get("baas.virtualAccounts.pixKeysHint")}
            onClick={(event) => {
              event.stopPropagation();
              onPixKeys(account);
            }}
          >
            <TbKey className="h-4 w-4" />
          </button>
        ) : null}
        <GridHistoryDetailButton
          onClick={(event) => {
            event.stopPropagation();
            onOpen(account);
          }}
        />
      </div>
    </GridHistoryRow>
  );
};

export const BaasVirtualAccountsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { addToast } = useToast();
  const { selectedInstance, instances, listInstanceFilter, selectInstanceForPeerOps } =
    useBaasInstance();
  const { can, capabilities, loading: capabilitiesLoading } = useBaasCapabilities();
  const isLoading = useBaasPageLoading(selectedInstance?.id);

  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [createOpen, setCreateOpen] = useState(false);
  const [createEntityId, setCreateEntityId] = useState("");
  const [pixAccount, setPixAccount] = useState(null);

  const canView = can("accounts.view");
  const canViewPix = can("processing.pix.view");
  const canWrite = can("accounts.create");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldOpen = params.get("create") === "1" || params.get("create") === "true";
    if (!shouldOpen) return;

    if (!canWrite) return;

    setCreateEntityId(params.get("entityId") || "");
    setCreateOpen(true);
    params.delete("create");
    params.delete("entityId");
    const nextSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : "",
    });
  }, [canWrite, history, location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedInstance) {
      setAccounts([]);
      setLoadError(null);
      return undefined;
    }

    setAccountsLoading(true);
    setLoadError(null);
    (async () => {
      try {
        // Dedicated GET /v1/accounts/virtuals — do not use fetchBaasAccounts, which
        // swallows virtuals failures into an empty merge (empty Contas virtuais with no toast).
        const [result, peerRoots] = await Promise.all([
          fetchBaasVirtualAccounts(selectedInstance),
          shouldFetchBaasPeerLists(selectedInstance, listInstanceFilter)
            ? fetchBaasPeerInstanceRoots(selectedInstance, {
                instanceId: resolveBaasListInstanceNumericId(
                  listInstanceFilter,
                  selectedInstance,
                  instances,
                ),
              }).catch(() => [])
            : Promise.resolve([]),
        ]);
        if (!cancelled) {
          const merged = [
            ...(Array.isArray(result?.items) ? result.items : []).map((row) =>
              stampBaasRowInstance(row, selectedInstance),
            ),
            ...(Array.isArray(peerRoots) ? peerRoots : []),
          ];
          setAccounts(merged);
          setLoadError(null);
        }
      } catch (error) {
        if (!cancelled) {
          setAccounts([]);
          setLoadError(error);
          addToast({
            type: "error",
            title:
              error?.detail
              || error?.message
              || intl.get("baas.accounts.virtualSection.loadFailed"),
          });
        }
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedInstance, listVersion, addToast, listInstanceFilter, instances]);

  const showInstanceColumn = canUseBaasGlobalInstanceFilter(selectedInstance);

  const visibleAccounts = useMemo(
    () =>
      accounts.filter((row) =>
        rowMatchesBaasListInstanceFilter(
          row,
          listInstanceFilter,
          selectedInstance,
          instances,
        ),
      ),
    [accounts, listInstanceFilter, selectedInstance, instances],
  );

  const paginatedItems = useMemo(
    () => paginateList(visibleAccounts, page, rowsPerPage),
    [visibleAccounts, page, rowsPerPage],
  );

  const openAccountDetail = (account) => {
    if (shouldPinPeerForAccountOps(account, selectedInstance)) {
      selectInstanceForPeerOps?.(
        account.instanceId ?? account.baasInstanceId,
        selectedInstance?.environment,
      );
    }
    history.push(buildBaasAccountDetailPath(account.id));
  };

  const handleCreated = (account) => {
    setListVersion((current) => current + 1);
    setPage(1);
    if (account?.id) {
      history.push(buildBaasAccountDetailPath(account.id));
    }
  };

  if (isLoading || capabilitiesLoading || accountsLoading) {
    return <BaasTablePageSkeleton columns={showInstanceColumn ? 6 : 5} />;
  }

  if (!hasBaasBankingRails(capabilities)) {
    return <Redirect to="/baas/inicio" />;
  }

  if (!canView) {
    return (
      <div className="baas-virtual-accounts min-w-0 space-y-5">
        <header>
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.nav.virtualAccounts")}
          </h1>
        </header>
        <div className="app-card px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.home.capabilities.permissionDenied")}
        </div>
      </div>
    );
  }

  return (
    <div className="baas-virtual-accounts min-h-0 min-w-0 h-auto space-y-4">
      <header className="min-w-0 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.nav.virtualAccounts")}
          </h1>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.virtualAccounts.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-outline w-auto inline-flex items-center gap-2 !h-8 !min-h-8 !text-xs"
            onClick={() => setListVersion((current) => current + 1)}
          >
            <TbRefresh className="h-3.5 w-3.5" aria-hidden />
            {intl.get("baas.pix.refresh")}
          </button>
          {canWrite ? (
            <button
              type="button"
              className="btn-primary w-auto inline-flex items-center gap-2 !h-8 !min-h-8 !text-xs"
              onClick={() => {
                setCreateEntityId("");
                setCreateOpen(true);
              }}
            >
              <TbCirclePlus className="h-3.5 w-3.5" aria-hidden />
              {intl.get("baas.accounts.virtualCreate.submit")}
            </button>
          ) : null}
        </div>
      </header>

      <p className="rounded-lg border border-[#e7a73f]/35 bg-[#e7a73f]/10 px-2.5 py-1.5 text-xs text-[#7a5418] dark:border-brand-gold-mid/40 dark:bg-brand-gold-mid/10 dark:text-brand-gold-mid">
        {intl.get("baas.virtualAccounts.pixFlowHint")}
      </p>

      {canWrite ? (
        <BaasVirtualAccountCreateModal
          open={createOpen}
          onClose={() => {
            setCreateOpen(false);
            setCreateEntityId("");
          }}
          initialEntityId={createEntityId}
          onCreated={handleCreated}
        />
      ) : null}

      <GridHistoryCard>
        {loadError ? (
          <div className="m-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
            {loadError?.detail
              || loadError?.message
              || intl.get("baas.accounts.virtualSection.loadFailed")}
          </div>
        ) : null}
        {!loadError && paginatedItems.length === 0 ? (
          <div className="m-4 space-y-3 text-center">
            <div className="app-empty-state">{intl.get("baas.accounts.virtualSection.empty")}</div>
            {canWrite ? (
              <button
                type="button"
                className="btn-primary w-auto inline-flex items-center gap-2"
                onClick={() => setCreateOpen(true)}
              >
                <TbCirclePlus className="h-4 w-4" aria-hidden />
                {intl.get("baas.accounts.virtualCreate.submit")}
              </button>
            ) : null}
          </div>
        ) : null}
        {!loadError && paginatedItems.length > 0 ? (
          <GridHistoryContainer
            gridCols={showInstanceColumn ? GRID_HISTORY_COLS_6 : GRID_HISTORY_COLS_5}
          >
            <VirtualAccountsHeader showInstanceColumn={showInstanceColumn} />
            {paginatedItems.map((account) => (
              <VirtualAccountRow
                key={account.id}
                account={account}
                onOpen={openAccountDetail}
                onPixKeys={setPixAccount}
                canViewPix={canViewPix}
                showInstanceColumn={showInstanceColumn}
              />
            ))}
          </GridHistoryContainer>
        ) : null}

        <GridPagination
          total={visibleAccounts.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(value) => {
            setRowsPerPage(value);
            setPage(1);
          }}
          currentPageCount={paginatedItems.length}
          itemLabel={intl.get("baas.virtualAccounts.paginationLabel")}
        />
      </GridHistoryCard>

      {canViewPix && pixAccount ? (
        <BaasAccountPixKeysModal
          open={Boolean(pixAccount)}
          onClose={() => setPixAccount(null)}
          accountId={pixAccount.id}
          account={pixAccount}
        />
      ) : null}
    </div>
  );
};

export default BaasVirtualAccountsPage;
